import { useTranslation } from "next-i18next";
import classNames from "classnames";

// Styles
import Styles from "./TransporterCardTopSection.module.scss";

// Types
import { TransporterCardTopSection as TransporterCardTopSectionTypes } from "@vahak/core/dist/custom-types/cards";

// Icons
import ProfileVerifyTag from "@vahak/core/dist/icons/up_verified_profile.svg";
import GstVerified from "@vahak/core/dist/icons/GST_verified.svg";
import AadharVerified from "@vahak/core/dist/icons/aadhar_verified_profile.svg";

import ConnectionIcon from "@vahak/core/dist/icons/connections.svg";
import ViewsIcon from "@vahak/core/dist/icons/views.svg";
import YearsIcon from "@vahak/core/dist/icons/years_lorry.svg";

// Components
import EllipsisTooltip from "@vahak/core/dist/components/ellipsis-tooltip/EllipsisTooltip";
import RatingStars from "@vahak/core/dist/components/rating-stars/RatingStars";

import Col from "@vahak/core/dist/components/grid/col/Col";
import Row from "@vahak/core/dist/components/grid/row/Row";
import DotSeparated from "@vahak/core/dist/components/dot-separated/DotSeparated";
import AppImg from "../../../components/utils/AppImg";
import { getImageNameFromURL } from "../../../utils/getImageNameFromURL";
import Flex from "@vahak/core-ui/dist/layout/Flex";
import IfCanAccess from "../../admin/IfCanAccess/IfCanAccess";
import AgentBadge from "../../admin/badge/AgentBadge";

const TransporterCardTopSection = ({
    ratings,
    isAadharVerified,
    isGstVerified,
    companyName,
    jobTitle,
    companyLocation,
    totalConnections,
    totalViews,
    companyRunningFrom,
    logo,
    agentDetails
}: TransporterCardTopSectionTypes) => {
    const { t } = useTranslation(["common"]);

    const profileVerification =
        isGstVerified && isAadharVerified ? (
            <ProfileVerifyTag />
        ) : isGstVerified && !isAadharVerified ? (
            <GstVerified />
        ) : !isGstVerified && isAadharVerified ? (
            <AadharVerified />
        ) : (
            ""
        );

    const calculateNodes = () => {
        let arr = [];

        if (totalConnections) {
            arr.push(
                <p className={Styles.connections}>
                    <ConnectionIcon /> {totalConnections} {t("common:connections")}
                </p>
            );
        }

        if (totalViews) {
            arr.push(
                <p className={Styles.connections}>
                    <ViewsIcon /> {totalViews} {t("common:views")}
                </p>
            );
        }

        if (companyRunningFrom) {
            arr.push(
                <p className={Styles.connections}>
                    <YearsIcon /> {companyRunningFrom}+ {t("common:years")}
                </p>
            );
        }
        return arr;
    };

    const nodes = calculateNodes();

    return (
        <div className={Styles.main}>
            <Row className={Styles.profileDetailsRow} noGutters>
                <Col className={Styles.profileImageCol}>
                    <div className={Styles.image}>
                        <AppImg src={getImageNameFromURL(logo) || `default.png`} alt="" width="75px" height="75px" />
                    </div>
                </Col>
                <Col className={Styles.profileTextCol}>
                    <h3 className={Styles.name}>
                        <EllipsisTooltip
                            textValue={companyName || ""}
                            greaterThanValue={22}
                            mainClass={Styles.nameEllipsis}
                        />
                        {profileVerification}
                    </h3>
                    <p className={Styles.jobTitle}>
                        <Row noGutters className={Styles.alignCenter}>
                            {(ratings || 0) > 1 && (
                                <Col className={Styles.ratingsCol}>
                                    <RatingStars className={Styles.starIcons} rating={ratings || 0} />
                                    <span>{ratings.toFixed(1)}</span>
                                </Col>
                            )}
                            <Col>
                                <Flex gap={5} alignItems="center">
                                    <EllipsisTooltip
                                        textValue={jobTitle || ""}
                                        greaterThanValue={25}
                                        mainClass={Styles.jobTitleEllipsis}
                                    />
                                    <IfCanAccess action="" allowCsUser>
                                        <AgentBadge type="tso" data={agentDetails} />
                                    </IfCanAccess>
                                </Flex>
                            </Col>
                        </Row>
                    </p>
                    <p className={Styles.location}></p>
                    <p className={Styles.location}>{companyLocation}</p>
                </Col>
            </Row>
            <Row className={Styles.profileDetailsRow} noGutters>
                <div className={classNames(Styles.profileTextCol, Styles.oneLineInfo)}>
                    {nodes.length && nodes.length > 0 ? (
                        <DotSeparated dotStyleClassName={Styles.dot} nodes={nodes} />
                    ) : (
                        // This was done to remove the imbalance when there is no info to show
                        <div style={{ marginTop: "14px" }}></div>
                    )}
                </div>
            </Row>
        </div>
    );
};

export default TransporterCardTopSection;
